/**
 * Deferred loader for surviving third-party JavaScript (INT-1785349119).
 *
 * Non-critical third-party tags live inside `<template data-defer-third-party>`
 * blocks so the browser never fetches or executes them during initial render.
 * This loader hydrates those templates on the first of:
 *
 *   1. a genuine user interaction (pointer / touch / key / wheel / scroll),
 *   2. browser idle time after the `load` event (requestIdleCallback),
 *   3. a hard timeout backstop, so tags still fire for users who neither
 *      interact nor reach a quiet main thread.
 *
 * Hydration preserves document order across every template and waits for
 * external scripts to finish before running the next node, so tag snippets
 * that depend on an earlier one (config JSON, dataLayer seeds) still work.
 *
 * Manual control: window.CCThirdParty.load() forces hydration immediately.
 */
(function () {
  'use strict';

  var TEMPLATE_SELECTOR = 'template[data-defer-third-party]';
  var IDLE_TIMEOUT = window.CC_THIRD_PARTY_IDLE_TIMEOUT || 2500;
  var HARD_TIMEOUT = window.CC_THIRD_PARTY_HARD_TIMEOUT || 6000;
  var INTERACTIONS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
  var LISTENER_OPTS = { passive: true, capture: true };

  var started = false;
  var hardTimer = null;

  function detachTriggers() {
    for (var i = 0; i < INTERACTIONS.length; i++) {
      window.removeEventListener(INTERACTIONS[i], load, LISTENER_OPTS);
    }
    if (hardTimer) {
      clearTimeout(hardTimer);
      hardTimer = null;
    }
  }

  function collectNodes() {
    var templates = document.querySelectorAll(TEMPLATE_SELECTOR);
    var nodes = [];

    for (var i = 0; i < templates.length; i++) {
      var template = templates[i];
      if (template.hasAttribute('data-defer-hydrated')) continue;
      template.setAttribute('data-defer-hydrated', '');

      var children;
      if (template.content) {
        children = template.content.childNodes;
      } else {
        // Legacy browsers without HTMLTemplateElement support.
        var host = document.createElement('div');
        host.innerHTML = template.innerHTML;
        children = host.childNodes;
      }

      for (var j = 0; j < children.length; j++) {
        nodes.push(children[j]);
      }
    }

    return nodes;
  }

  function injectScript(source, next) {
    var script = document.createElement('script');
    var attributes = source.attributes;

    for (var i = 0; i < attributes.length; i++) {
      script.setAttribute(attributes[i].name, attributes[i].value);
    }

    var src = source.getAttribute('src');
    if (src) {
      // async=false keeps injected external scripts executing in insertion order.
      script.async = false;
      script.addEventListener('load', next, { once: true });
      script.addEventListener('error', next, { once: true });
      document.head.appendChild(script);
      return;
    }

    script.text = source.textContent || '';
    document.head.appendChild(script);
    next();
  }

  function runNode(nodes, index) {
    if (index >= nodes.length) {
      document.dispatchEvent(new CustomEvent('cc:third-party-loaded'));
      return;
    }

    var node = nodes[index];
    var next = function () {
      runNode(nodes, index + 1);
    };

    if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
      injectScript(node, next);
      return;
    }

    // Comments carry no behaviour; everything else (link, noscript, iframe,
    // inline markup used by tag snippets) is cloned into <head> as-is.
    if (node.nodeType === 1) {
      document.head.appendChild(node.cloneNode(true));
    }

    next();
  }

  function load() {
    if (started) return;
    started = true;
    detachTriggers();
    runNode(collectNodes(), 0);
  }

  function scheduleIdle() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: IDLE_TIMEOUT });
    } else {
      setTimeout(load, IDLE_TIMEOUT);
    }
  }

  window.CCThirdParty = {
    load: load,
    get loaded() {
      return started;
    }
  };

  // Theme editor: show every tag straight away so merchandisers can preview
  // sections without waiting on idle heuristics.
  if (window.Shopify && window.Shopify.designMode) {
    load();
    return;
  }

  for (var i = 0; i < INTERACTIONS.length; i++) {
    window.addEventListener(INTERACTIONS[i], load, LISTENER_OPTS);
  }

  if (document.readyState === 'complete') {
    scheduleIdle();
  } else {
    window.addEventListener('load', scheduleIdle, { once: true });
  }

  hardTimer = setTimeout(load, HARD_TIMEOUT);
})();
